import React, { Fragment, useCallback } from 'react';
import { Platform, View } from 'react-native';
import { styled, useResponsiveValue } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';

import { HubTutorialList } from './components/HubTutorialList';
import {
  BaseText,
  Filters,
  ScreenTopBar,
  Separator,
} from '@components';
import { IS_WEB, parseApiError } from '@utils';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { HubHeader } from './components/HubHeader';
import { ClearFiltersLabel } from './components/ClearFiltersLabel';
import { useTutorialsModel } from '@models/Tutorials/TutorialsModel';
import {
  AnalyticGlobalSearchEventTypeEnum,
  AnalyticsService,
  ToastService,
} from '@services';
import { Category } from '@models/Tutorials/CategoryModel';
import { screens, TabNavigatorNavigationPropType } from '@navigation';
import { HubTagsContainer } from '@components/business/HubTagsContainer';
import type { Tag } from '@models/Tags/TagModel';
import { useTagsModel } from '@models/Tags/TagsModel';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

const ContentContainer = styled(View)({
  flexDirection: 'row',
  flex: 1,
  mx: [null, '5%'],
});

const FiltersContainer = styled(View)({
  flex: 1,
  display: ['none', 'flex'],
});

const TutorialListContainer = styled(View)({
  flex: 3,
  pl: [null, '$8'],
});

const TextLabel = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xlarge',
});

type NavigationPropType = TabNavigatorNavigationPropType<'Hub'>;

export const HubScreen = observer(() => {
  const { t } = useTranslation();
  const tutorial = useTutorialsModel();
  const tags = useTagsModel();
  const nav = useNavigation<NavigationPropType>();
  const isSearchVisible = useResponsiveValue([false, true]);

  useNavOptionsSet(
    Platform.select({
      native: { header: () => <HubHeader /> },
      default: {},
    }),
  );

  useAsyncInit(async () => {
    try {
      await tutorial.list.fetch.run();
      await tags.recommended.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  useAsyncInit(async () => {
    if (IS_WEB) {
      try {
        await tutorial.categories.fetch.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }
  });

  async function refresh() {
    try {
      await tutorial.list.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function fetchMore() {
    try {
      await tutorial.list.fetchMore.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function onChangeFilterValue(item: Category, value: boolean) {
    item.setIsSelected(value);
    // for now we should fetch videos by one category/tag parameter
    tags.recommended.clearTagsSelection();

    refresh();
  }

  function onChangeTagValue(item: Tag) {
    item.setIsSelected(!item.isSelected);
    // for now we should fetch videos by one category/tag parameter
    tutorial.categories.clearFilters();

    refresh();
  }

  const navigateToSearch = useCallback((text: string) => {
    nav.navigate(screens.SearchTabNavigator, {
      screen: screens.SearchTutorial,
      params: { query: text },
    });
    AnalyticsService.trackGlobalSearch(
      text,
      AnalyticGlobalSearchEventTypeEnum.Static,
    );
  }, []);

  return (
    <Container>
      {IS_WEB && (
        <Fragment>
          <ScreenTopBar
            title={t('hub.title')}
            onSearchSubmit={navigateToSearch}
            isSearchVisible={isSearchVisible}
          />
          <Separator size="$8" />
        </Fragment>
      )}
      <ContentContainer>
        <FiltersContainer>
          {IS_WEB && <ClearFiltersLabel />}
          <Filters
            onChangeValue={onChangeFilterValue}
            data={tutorial.categories.filters}
            showsVerticalScrollIndicator={false}
            ListHeaderComponent={() => (
              <View>
                <TextLabel>{t('categories.title')}</TextLabel>
                <Separator size="$6" />
              </View>
            )}
            ListFooterComponent={() => (
              <HubTagsContainer onPress={onChangeTagValue} />
            )}
          />
        </FiltersContainer>
        <TutorialListContainer>
          <HubTutorialList
            list={tutorial.list}
            refresh={refresh}
            fetchMore={fetchMore}
          />
        </TutorialListContainer>
      </ContentContainer>
    </Container>
  );
});
